/**
 * NovaVoiceManager
 * Based on AWS sample implementation
 * Manages the Nova Sonic voice conversation flow
 *
 * Hands-free: once started, the mic streams for the whole conversation and
 * Nova Sonic decides when the user has finished speaking. Muting sends silence
 * rather than stopping the stream, because Sonic expects audio to keep flowing.
 * The user can talk over Kyva at any time; Sonic reports the barge-in and
 * playback stops.
 *
 * The transcript is built from the backend's messageSaved events -- the same
 * messages a refresh loads -- with the turn in progress shown live alongside.
 *
 * A dropped connection is not the end of the conversation: the manager reports
 * it (onConnectionLost) and the caller reconnects to the same session, which
 * the backend resumes with the earlier turns. Nova Sonic also caps one stream
 * at about eight minutes, so the manager renews the connection itself a little
 * before that, at a moment Kyva is not speaking.
 */

import AudioPlayer from './AudioPlayer';
import NovaAudioRecorder from './NovaAudioRecorder';
import { VoiceBrainBankSource, VoiceTranscriptEntry } from '../types';

// Used when no admin has chosen a voice default.
export const FALLBACK_VOICE_MODEL_ID = 'amazon.nova-2-sonic-v1:0';

// Voice mode runs on the Nova Sonic protocol, so every Sonic version (2, 2.5, ...) is a voice model.
export const isVoiceModel = (model?: { foundation_model_id?: string } | null): boolean =>
  !!model?.foundation_model_id?.toLowerCase().includes('sonic');

interface NovaVoiceEvent {
  event: {
    [key: string]: any;
  };
}

/** A message the backend has stored, as its messageSaved event reports it. */
export interface SavedVoiceMessage {
  messageId: string;
  role: 'user' | 'assistant';
  text: string;
  createdAt: string;
  interrupted?: boolean;
  sources?: VoiceBrainBankSource[];
}

export interface VoiceSessionData {
  sessionId: string;
  title: string;
  timestamp: string;
  fullConversationAudioUrl?: string;
}

// Renew the stream before Nova Sonic's ~8 minute limit, waiting for a pause in
// Kyva's speech, but never past the hard deadline.
const RENEW_AFTER_MS = 7 * 60 * 1000;
const RENEW_DEADLINE_MS = 7.75 * 60 * 1000;
const RENEW_CHECK_MS = 2000;

// Hands-free streams the mic continuously (~43 KB/s as base64 JSON). A
// browser never waits for the network before queuing more, so on an uplink
// slower than that the queue grows without limit -- and the server's keepalive
// pong waits behind it until the server gives up on the connection. The queue
// is capped: past this backlog (about 3 s of audio) mic chunks are skipped
// until it drains. Audio that late would be useless to the conversation anyway.
const MAX_UPLOAD_BACKLOG_BYTES = 128 * 1024;
// Report a building backlog in the console, for diagnosing a slow uplink.
const BACKLOG_WARN_BYTES = 32 * 1024;

// How long End waits, in the background, for the backend to confirm the
// conversation is saved before closing the socket regardless.
const FINALIZE_WAIT_MS = 10000;

// Sonic marks a barge-in with this text in place of a reply.
const isInterruption = (content: string): boolean => {
  if (!content.includes('interrupted')) return false;
  try {
    return JSON.parse(content)?.interrupted === true;
  } catch {
    return false;
  }
};

export default class NovaVoiceManager {
  private websocket: WebSocket | null = null;
  private audioPlayer: AudioPlayer;
  private audioRecorder: NovaAudioRecorder;
  private promptName: string | null = null;
  private audioContentName: string | null = null;
  // Whether the mic's audio content block is open on the stream.
  private audioContentOpen: boolean = false;
  private isActive: boolean = false;
  private isRecording: boolean = false;
  private muted: boolean = false;
  // Silent chunks by length, sent in place of mic audio while muted.
  private silenceCache: Map<number, string> = new Map();
  private currentAudioConfig: any = null;
  private sessionId: string | null = null;
  // Why the socket may close: the user ended the conversation, the backend
  // refused it (do not retry), or neither -- then it was lost and can resume.
  private endedByUser: boolean = false;
  private refused: boolean = false;
  private reconnecting: boolean = false;
  private renewTimer: number | null = null;
  private streamOpenedAt: number = 0;
  private skippedForBacklog: number = 0;
  private lastBacklogWarnAt: number = 0;
  // A BrainBank lookup is running: the renewal waits for its answer.
  private searchInFlight: boolean = false;

  // The turn in progress. Sonic sends each reply as a SPECULATIVE draft ahead
  // of the audio, then the FINAL text of what it said; the draft is shown
  // until the final text replaces it.
  private textBlockRole: 'user' | 'assistant' | null = null;
  private textBlockStage: 'SPECULATIVE' | 'FINAL' = 'FINAL';
  private liveUserText: string = "";
  private liveAssistantFinal: string = "";
  private liveAssistantDraft: string = "";
  private conversationHistory: VoiceTranscriptEntry[] = [];

  // Original callbacks
  public onStatusChange?: (status: string, type: 'connected' | 'disconnected' | 'error') => void;
  public onAudioPlaybackStart?: () => void;
  public onAudioPlaybackEnd?: () => void;
  public onSessionEnd?: (sessionData?: VoiceSessionData) => void;

  // Live transcript: the turn in progress plus everything saved so far.
  public onCurrentTextUpdate?: (userText: string, assistantText: string, history: VoiceTranscriptEntry[]) => void;

  // The backend created the conversation's session.
  public onSessionCreated?: (sessionId: string) => void;
  // The backend stored a message; it is in the conversation from now on.
  public onMessageSaved?: (message: SavedVoiceMessage) => void;
  // The user talked over Kyva, and playback was cut.
  public onInterrupted?: () => void;
  // The connection dropped, or is being renewed, mid-conversation: reconnect.
  public onConnectionLost?: (reason: 'dropped' | 'renewal') => void;

  // BrainBank callbacks: which BrainBank(s) the conversation uses, and searches
  // starting and finishing.
  public onBrainBankSelected?: (brainBanks: Array<{ id: string; name: string }>, pinned: boolean) => void;
  public onBrainBankSearch?: (
    status: 'started' | 'finished' | 'failed',
    info?: { kind?: 'brainbank' | 'document' | 'spreadsheet' | 'open'; document?: string }
  ) => void;
  // The backend refused or lost the conversation; the message is fit to show.
  public onError?: (message: string) => void;

  constructor() {
    this.audioPlayer = new AudioPlayer();
    this.audioRecorder = new NovaAudioRecorder();
  }

  /**
   * Start the voice conversation session
   */
  async startSession(websocketUrl: string, systemPrompt?: string): Promise<void> {
    try {
      // A new conversation starts clean; a reconnect keeps its transcript.
      if (!this.reconnecting) {
        this.conversationHistory = [];
      }
      this.searchInFlight = false;

      // Initialize audio player
      await this.audioPlayer.start();

      // Connect to WebSocket
      this.websocket = new WebSocket(websocketUrl);

      this.endedByUser = false;
      this.refused = false;
      this.skippedForBacklog = 0;
      const socket = this.websocket;

      this.websocket.onopen = () => {
        // Ended (End or X) while it was still connecting: never start it.
        if (this.endedByUser || socket !== this.websocket) {
          socket.close();
          return;
        }
        console.log('WebSocket connected');
        this.isActive = true;
        this.reconnecting = false;
        this.streamOpenedAt = Date.now();
        this.scheduleRenewal();
        this.onStatusChange?.('Connected', 'connected');

        // Start the Nova Sonic session
        this.initializeNovaSession(systemPrompt);
      };

      this.websocket.onmessage = (event: MessageEvent) => {
        try {
          const data: NovaVoiceEvent = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.onStatusChange?.('Connection error', 'error');
      };

      this.websocket.onclose = (event: CloseEvent) => {
        // The close code says who ended it: 1000/1001 a clean close, 1006 the
        // network or a proxy in between, 1011 the server.
        console.warn(
          `[Nova] Voice socket closed: code=${event.code} reason=${event.reason || '-'} ` +
          `uploadBacklog=${Math.round((socket?.bufferedAmount || 0) / 1024)}KB ` +
          `chunksSkippedForBacklog=${this.skippedForBacklog}`
        );
        // Only the current socket closing matters; a replaced one is expected.
        if (socket !== this.websocket) return;
        if (this.endedByUser) return;

        const wasLive = this.isActive || this.reconnecting;
        this.isActive = false;
        if (!wasLive) {
          // Never opened (refused at the handshake, or no network): end it
          // cleanly instead of leaving a live-looking window with the mic on.
          this.onError?.('Could not connect to voice. Please try again.');
          this.cleanup();
          this.onSessionEnd?.();
          return;
        }

        if (this.refused || !this.sessionId) {
          // Refused, or lost before it ever had a session: nothing to resume.
          this.onStatusChange?.('Disconnected', 'disconnected');
          this.cleanup();
          this.onSessionEnd?.();
          return;
        }
        this.dropStream();
        this.onConnectionLost?.('dropped');
      };

    } catch (error) {
      console.error('Failed to start session:', error);
      throw error;
    }
  }

  /**
   * Initialize the Nova Sonic session with proper event sequence
   */
  private initializeNovaSession(systemPrompt?: string): void {
    // 1. Send sessionStart
    this.sendEvent({
      event: {
        sessionStart: {
          inferenceConfiguration: {
            maxTokens: 1024,
            topP: 0.95,
            temperature: 0.7,
          },
        },
      },
    });

    // 2. Send promptStart. The backend adds the tool configuration.
    this.promptName = crypto.randomUUID();
    this.sendEvent({
      event: {
        promptStart: {
          promptName: this.promptName,
          textOutputConfiguration: {
            mediaType: 'text/plain',
          },
          audioOutputConfiguration: {
            mediaType: 'audio/lpcm',
            sampleRateHertz: 24000,
            sampleSizeBits: 16,
            channelCount: 1,
            voiceId: 'tiffany',
            encoding: 'base64',
            audioType: 'SPEECH',
          },
        },
      },
    });

    // 3. Send system prompt. The backend replaces the text with its own; this
    // block only gives it a place in the protocol.
    const defaultPrompt = `You are a friendly assistant. Keep your responses short, generally two or three sentences for chatty scenarios.`;
    const prompt = systemPrompt || defaultPrompt;
    this.sendSystemPrompt(prompt);

    // 4. Start audio content
    this.startAudioContent();
  }

  /**
   * Send system prompt
   */
  private sendSystemPrompt(prompt: string): void {
    const systemContentName = crypto.randomUUID();

    // Content start
    this.sendEvent({
      event: {
        contentStart: {
          promptName: this.promptName,
          contentName: systemContentName,
          type: 'TEXT',
          interactive: true,
          textInputConfiguration: {
            mediaType: 'text/plain',
          },
        },
      },
    });

    // Text input
    this.sendEvent({
      event: {
        textInput: {
          promptName: this.promptName,
          contentName: systemContentName,
          content: prompt,
          role: 'SYSTEM',
        },
      },
    });

    // Content end
    this.sendEvent({
      event: {
        contentEnd: {
          promptName: this.promptName,
          contentName: systemContentName,
        },
      },
    });
  }

  /**
   * Start audio content stream
   */
  private startAudioContent(): void {
    this.audioContentName = crypto.randomUUID();
    this.audioContentOpen = true;
    this.sendEvent({
      event: {
        contentStart: {
          promptName: this.promptName,
          contentName: this.audioContentName,
          type: 'AUDIO',
          role: 'USER',
          interactive: true,
          audioInputConfiguration: {
            mediaType: 'audio/lpcm',
            sampleRateHertz: 16000,
            sampleSizeBits: 16,
            channelCount: 1,
            audioType: 'SPEECH',
            encoding: 'base64',
          },
        },
      },
    });
  }

  private closeAudioContent(): void {
    if (this.promptName && this.audioContentName && this.audioContentOpen) {
      this.sendEvent({
        event: {
          contentEnd: {
            promptName: this.promptName,
            contentName: this.audioContentName,
          },
        },
      });
    }
    this.audioContentOpen = false;
  }

  // A chunk of silence the same length as a base64 mic chunk.
  private silenceLike(base64Audio: string): string {
    const length = base64Audio.length;
    let silence = this.silenceCache.get(length);
    if (!silence) {
      const padding = base64Audio.endsWith('==') ? 2 : base64Audio.endsWith('=') ? 1 : 0;
      const bytes = (length / 4) * 3 - padding;
      silence = btoa(String.fromCharCode(...new Uint8Array(bytes)));
      this.silenceCache.set(length, silence);
    }
    return silence;
  }

  /**
   * Start streaming the mic for the rest of the conversation.
   */
  async startRecording(): Promise<void> {
    if (this.isRecording) return;
    try {
      // The block is opened when the socket opens; reopen it only if something
      // closed it in between.
      if (this.promptName && !this.audioContentOpen) {
        this.startAudioContent();
      }
      this.muted = false;
      await this.audioRecorder.start((base64Audio: string) => {
        if (this.promptName && this.audioContentName && this.audioContentOpen) {
          if (!this.uploadHasRoom()) return;
          this.sendEvent({
            event: {
              audioInput: {
                promptName: this.promptName,
                contentName: this.audioContentName,
                content: this.muted ? this.silenceLike(base64Audio) : base64Audio,
                role: 'USER',
              },
            },
          });
        }
      });
      // Ended while the mic was starting up: release it straight away.
      if (this.endedByUser || !this.websocket) {
        this.audioRecorder.stop();
        return;
      }
      this.isRecording = true;
    } catch (error) {
      console.error('Failed to start recording:', error);
      throw error;
    }
  }

  // Whether another mic chunk may be queued: false while the upload backlog
  // is over the cap (see MAX_UPLOAD_BACKLOG_BYTES).
  private uploadHasRoom(): boolean {
    const backlog = this.websocket?.bufferedAmount || 0;
    const now = Date.now();
    if (backlog > BACKLOG_WARN_BYTES && now - this.lastBacklogWarnAt > 5000) {
      this.lastBacklogWarnAt = now;
      console.warn(
        `[Nova] Upload falling behind: ${Math.round(backlog / 1024)}KB of audio waiting to send ` +
        `(${this.skippedForBacklog} chunks skipped so far)`
      );
    }
    if (backlog > MAX_UPLOAD_BACKLOG_BYTES) {
      this.skippedForBacklog += 1;
      return false;
    }
    return true;
  }

  /**
   * Mute or unmute the mic. The stream keeps running either way.
   */
  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /**
   * Stop the mic and tell Sonic the user's audio has ended.
   */
  stopRecording(): void {
    this.audioRecorder.stop();
    this.isRecording = false;
    this.closeAudioContent();
  }

  /**
   * Resume the conversation on a new connection, keeping the transcript.
   * The URL carries the session id, so the backend continues that session.
   */
  async reconnect(websocketUrl: string): Promise<void> {
    this.reconnecting = true;
    await this.startSession(websocketUrl);
    await this.startRecording();
  }

  isReconnecting(): boolean {
    return this.reconnecting;
  }

  /** A conversation exists: connecting, live, or reconnecting. */
  hasConversation(): boolean {
    return !!this.websocket || this.isActive || this.reconnecting;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  // Let go of the current stream (socket, mic, playback) but keep the
  // conversation -- transcript and session -- for the next one.
  private dropStream(): void {
    this.clearRenewal();
    this.audioRecorder.stop();
    // The player keeps running: speech already received finishes playing
    // while the next connection comes up.
    this.isRecording = false;
    if (this.websocket) {
      // Detached first, so closing it on purpose is not reported as a loss.
      const old = this.websocket;
      this.websocket = null;
      old.onopen = null;
      old.onmessage = null;
      old.onerror = null;
      old.onclose = null;
      try {
        old.close();
      } catch {
        // already closed
      }
    }
    this.promptName = null;
    this.audioContentName = null;
    this.audioContentOpen = false;
    this.currentAudioConfig = null;
    this.textBlockRole = null;
    this.textBlockStage = 'FINAL';
    this.liveUserText = '';
    this.liveAssistantFinal = '';
    this.liveAssistantDraft = '';
    this.reconnecting = true;
    this.emitTextUpdate();
  }

  private scheduleRenewal(): void {
    this.clearRenewal();
    this.renewTimer = window.setTimeout(() => this.renewWhenQuiet(), RENEW_AFTER_MS);
  }

  private clearRenewal(): void {
    if (this.renewTimer !== null) {
      window.clearTimeout(this.renewTimer);
      this.renewTimer = null;
    }
  }

  // Renew between Kyva's replies, so no answer is cut off by the switch.
  private renewWhenQuiet(): void {
    this.renewTimer = null;
    if (!this.isActive || this.endedByUser) return;
    // Kyva speaking, or a BrainBank lookup whose answer is still coming.
    const speaking = this.currentAudioConfig !== null || this.searchInFlight;
    const pastDeadline = Date.now() - this.streamOpenedAt >= RENEW_DEADLINE_MS;
    if (speaking && !pastDeadline) {
      this.renewTimer = window.setTimeout(() => this.renewWhenQuiet(), RENEW_CHECK_MS);
      return;
    }
    // End the old stream properly so the backend saves it, then hand over.
    if (this.promptName) {
      this.closeAudioContent();
      this.sendEvent({ event: { promptEnd: { promptName: this.promptName } } });
    }
    this.sendEvent({ event: { sessionEnd: {} } });
    this.isActive = false;
    this.dropStream();
    this.onConnectionLost?.('renewal');
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: NovaVoiceEvent): void {
    if (!data.event) return;

    const event = data.event;
    const eventType = Object.keys(event)[0];

    try {
      switch (eventType) {
        case 'sessionCreated':
          if (event.sessionCreated?.sessionId) {
            this.sessionId = event.sessionCreated.sessionId;
            this.onSessionCreated?.(this.sessionId!);
          }
          break;
        case 'sessionFinalized':
          // Consumed by waitForSessionFinalized
          break;
        case 'textOutput':
          this.handleTextOutput(event.textOutput);
          break;
        case 'audioOutput':
          this.handleAudioOutput(event.audioOutput);
          break;
        case 'contentStart':
          if (event.contentStart.type === 'AUDIO') {
            // Store audio output configuration
            this.currentAudioConfig = event.contentStart.audioOutputConfiguration;
            this.onAudioPlaybackStart?.();
          } else if (event.contentStart.type === 'TEXT') {
            this.startTextBlock(event.contentStart);
          }
          break;
        case 'contentEnd':
          if (event.contentEnd.type === 'AUDIO') {
            // Clear audio config when audio content ends
            this.currentAudioConfig = null;
            this.onAudioPlaybackEnd?.();
          }
          break;
        case 'messageSaved':
          this.handleMessageSaved(event.messageSaved);
          break;
        case 'brainbankSelected':
          this.onBrainBankSelected?.(
            event.brainbankSelected?.brainBanks || [],
            !!event.brainbankSelected?.pinned
          );
          break;
        case 'brainbankSearch': {
          const status = event.brainbankSearch?.status;
          this.searchInFlight = status === 'started';
          if (status === 'started' || status === 'finished' || status === 'failed') {
            this.onBrainBankSearch?.(status, {
              kind: event.brainbankSearch?.kind,
              document: event.brainbankSearch?.document,
            });
          }
          break;
        }
        case 'error':
          console.error('[Nova] Error from voice backend:', event.error);
          this.refused = true;
          this.onError?.(event.error?.message || 'Voice conversation failed. Please try again.');
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error handling message:', error);
    }
  }

  private startTextBlock(contentStart: any): void {
    const role = (contentStart.role || '').toLowerCase() === 'user' ? 'user' : 'assistant';
    let stage: 'SPECULATIVE' | 'FINAL' = 'FINAL';
    try {
      if (contentStart.additionalModelFields) {
        const fields = JSON.parse(contentStart.additionalModelFields);
        if (fields?.generationStage === 'SPECULATIVE') stage = 'SPECULATIVE';
      }
    } catch {
      // A block without readable fields is final text
    }
    this.textBlockRole = role;
    this.textBlockStage = stage;
    // A new draft replaces the last one; final text replaces the draft it confirms.
    if (role === 'assistant') {
      this.liveAssistantDraft = '';
    }
  }

  /**
   * Handle text output from Nova Sonic: the turn in progress, shown live.
   */
  private handleTextOutput(textOutput: any): void {
    const content: string = textOutput.content || '';
    const role = (textOutput.role || this.textBlockRole || '').toLowerCase() === 'user' ? 'user' : 'assistant';

    if (isInterruption(content)) {
      // The user talked over Kyva: stop playback now, and drop the unspoken draft.
      this.audioPlayer.bargeIn();
      this.liveAssistantDraft = '';
      this.onInterrupted?.();
      this.emitTextUpdate();
      return;
    }

    if (role === 'user') {
      this.liveUserText = this.joinText(this.liveUserText, content);
    } else if (this.textBlockStage === 'SPECULATIVE') {
      this.liveAssistantDraft = this.joinText(this.liveAssistantDraft, content);
    } else {
      this.liveAssistantFinal = this.joinText(this.liveAssistantFinal, content);
      this.liveAssistantDraft = '';
    }
    this.emitTextUpdate();
  }

  private joinText(current: string, next: string): string {
    if (!current) return next;
    if (!next) return current;
    return /\s$/.test(current) || /^\s/.test(next) ? current + next : `${current} ${next}`;
  }

  private liveAssistantText(): string {
    return this.joinText(this.liveAssistantFinal, this.liveAssistantDraft).trim();
  }

  private emitTextUpdate(): void {
    this.onCurrentTextUpdate?.(
      this.liveUserText.trim(),
      this.liveAssistantText(),
      this.conversationHistory
    );
  }

  /**
   * A message the backend stored: it joins the transcript, and the live text
   * for that side of the conversation starts over.
   */
  private handleMessageSaved(saved: SavedVoiceMessage): void {
    if (!saved?.messageId || this.conversationHistory.some((m) => m.id === saved.messageId)) {
      return;
    }
    const entry: VoiceTranscriptEntry = {
      id: saved.messageId,
      role: saved.role === 'user' ? 'user' : 'assistant',
      text: saved.text,
      createdAt: saved.createdAt,
      interrupted: !!saved.interrupted,
      sources: saved.sources && saved.sources.length ? saved.sources : undefined,
    };
    this.conversationHistory = [...this.conversationHistory, entry];
    if (entry.role === 'user') {
      this.liveUserText = '';
    } else {
      this.liveAssistantFinal = '';
      this.liveAssistantDraft = '';
    }
    this.onMessageSaved?.(saved);
    this.emitTextUpdate();
  }

  /**
   * Handle audio output from Nova Sonic
   */
  private handleAudioOutput(audioOutput: any): void {
    // Ended: Kyva stops talking at once, whatever is still arriving.
    if (this.endedByUser) return;
    try {
      // Only play audio if we have a valid audio configuration (AWS sample pattern)
      if (!this.currentAudioConfig) {
        return;
      }

      // Convert base64 to Float32Array
      const audioData = this.base64ToFloat32Array(audioOutput.content);

      // Play audio
      this.audioPlayer.playAudio(audioData);
    } catch (error) {
      console.error('[Nova] Error playing audio:', error);
    }
  }

  /**
   * Convert base64 LPCM to Float32Array
   */
  private base64ToFloat32Array(base64String: string): Float32Array {
    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const int16Array = new Int16Array(bytes.buffer);
    const float32Array = new Float32Array(int16Array.length);

    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768.0;
    }

    return float32Array;
  }

  /**
   * Send an event to the WebSocket
   */
  private sendEvent(event: NovaVoiceEvent): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(event));
    }
  }

  /**
   * End the session
   */
  endSession(): void {
    this.endedByUser = true;
    this.clearRenewal();
    if (!this.isActive) {
      // Ended while connecting or reconnecting: there is no live stream to
      // close properly -- cancel it.
      if (this.reconnecting || this.websocket) {
        this.reconnecting = false;
        this.cleanup();
        this.onSessionEnd?.();
      }
      return;
    }

    // Tell the backend to save and finish, on the socket that is ending.
    if (this.promptName) {
      this.closeAudioContent();
      this.sendEvent({ event: { promptEnd: { promptName: this.promptName } } });
    }
    this.sendEvent({ event: { sessionEnd: {} } });

    // For the user the conversation ends now: mic, voice and state go at once,
    // and the socket is detached so a new conversation can start straight
    // away. Only the detached socket waits -- briefly, in the background --
    // for the backend to confirm the save (title, recording link).
    const endingSocket = this.websocket;
    const endingSessionId = this.sessionId;
    this.websocket = null;
    this.isActive = false;
    this.cleanup();

    if (!endingSocket) {
      this.onSessionEnd?.();
      return;
    }
    endingSocket.onclose = null;
    endingSocket.onerror = null;
    endingSocket.onmessage = null;
    this.waitForSessionFinalized(endingSocket, FINALIZE_WAIT_MS).then((sessionData) => {
      try {
        endingSocket.close();
      } catch {
        // already closed
      }
      this.onSessionEnd?.(
        sessionData || (endingSessionId ? { sessionId: endingSessionId, title: '', timestamp: '' } : undefined)
      );
    });
  }

  // Wait on one socket for the backend's sessionFinalized, up to a limit.
  private waitForSessionFinalized(socket: WebSocket, timeoutMs: number): Promise<VoiceSessionData | undefined> {
    return new Promise((resolve) => {
      let resolved = false;
      const finish = (data?: VoiceSessionData) => {
        if (resolved) return;
        resolved = true;
        socket.removeEventListener('message', listener);
        socket.removeEventListener('close', onClose);
        resolve(data);
      };
      const listener = (event: MessageEvent) => {
        try {
          const data: NovaVoiceEvent = JSON.parse(event.data);
          if (data.event && data.event.sessionFinalized) {
            finish(data.event.sessionFinalized);
          }
        } catch (e) {
          // ignore parse errors
        }
      };
      // The server closing first means no confirmation is coming.
      const onClose = () => finish(undefined);
      socket.addEventListener('message', listener);
      socket.addEventListener('close', onClose);
      setTimeout(() => finish(undefined), timeoutMs);
    });
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.clearRenewal();
    this.reconnecting = false;
    this.searchInFlight = false;
    this.audioRecorder.stop();
    this.audioPlayer.stop();
    this.isRecording = false;
    this.muted = false;

    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }

    // Don't set isActive = false here, let endSession handle it so request flow completes
    // unless called from unexpected close
    this.promptName = null;
    this.audioContentName = null;
    this.audioContentOpen = false;
    this.currentAudioConfig = null;
    // The conversation is over: a new one must never resume this session.
    this.sessionId = null;

    this.textBlockRole = null;
    this.textBlockStage = 'FINAL';
    this.liveUserText = '';
    this.liveAssistantFinal = '';
    this.liveAssistantDraft = '';
    this.conversationHistory = [];
  }

  /**
   * Check if session is active
   */
  isSessionActive(): boolean {
    return this.isActive;
  }

  /** Replace the transcript, e.g. with the stored conversation after a reconnect. */
  public setConversationHistory(history: VoiceTranscriptEntry[]): void {
    this.conversationHistory = [...history];
    this.emitTextUpdate();
  }

  public getConversationHistory(): VoiceTranscriptEntry[] {
    return [...this.conversationHistory];
  }
}
